import React from "react";
import PropTypes from "prop-types";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import { getStatus } from "../../../../utils/AuxFunctions";
import { FormatInt } from "../../../../utils/FormatData";
import { getCampaignDetailReportData } from "../utils/campaignDetailReportData";
import dayjs from "dayjs";

Font.registerEmojiSource({
  format: "png",
  url: "https://cdn.jsdelivr.net/npm/emoji-datasource-apple@15.0.1/img/apple/64/",
  withVariationSelectors: true,
});
const styles = StyleSheet.create({
  page: {
    backgroundColor: "#ffffff",
    color: "#1f2937",
    fontSize: 10,
    padding: 28,
  },
  header: {
    borderBottom: "1 solid #d1d5db",
    marginBottom: 16,
    paddingBottom: 10,
  },
  titleRow: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  title: {
    color: "#374151",
    fontSize: 18,
    fontWeight: 700,
  },
  badge: {
    backgroundColor: "#eef2ff",
    borderRadius: 4,
    color: "#374151",
    fontSize: 9,
    padding: "4 8",
    textTransform: "uppercase",
  },
  sectionTitle: {
    color: "#111827",
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 8,
  },
  campaignMessage: {
    backgroundColor: "#f3f4f6",
    borderRadius: 6,
    marginBottom: 12,
    padding: 10,
  },
  muted: {
    color: "#6b7280",
  },
  badgeStatus: {
    borderRadius: 4,
    fontSize: 9,
    padding: "4 8",
    textTransform: "uppercase",
  },
  bold: {
    fontWeight: 700,
  },
  cardsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
  },
  card: {
    backgroundColor: "#f3f4f6",
    borderRadius: 6,
    padding: 10,
    width: "31.8%",
  },
  cardValue: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 3,
  },
  progressBox: {
    backgroundColor: "#f3f4f6",
    borderRadius: 6,
    marginBottom: 12,
    padding: 10,
  },
  progressHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  progressTrack: {
    backgroundColor: "#d1d5db",
    borderRadius: 8,
    height: 8,
    marginBottom: 6,
    width: "100%",
  },
  progressFill: {
    backgroundColor: "#22c55e",
    borderRadius: 8,
    height: 8,
  },
  detailBox: {
    backgroundColor: "#f3f4f6",
    borderRadius: 6,
    padding: 10,
  },
  detailRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: "5 8",
  },
  detailRowAlt: {
    backgroundColor: "#e5e7eb",
  },
  detailLabel: {
    color: "#6b7280",
    fontWeight: 700,
  },
  footer: {
    borderTop: "1 solid #e5e7eb",
    color: "#6b7280",
    fontSize: 8,
    marginTop: 16,
    paddingTop: 8,
  },
});

const CampaignDetailPdfDocument = (props) => {
  const {
    title = "Detalle de campaña",
    type = "sms",
    idStatus,
    nameCampaign,
    data = {},
  } = props;
  const { cards, details } = getCampaignDetailReportData({
    data,
    type,
  });

  const STATUS_STYLES = {
    1: {
      backgroundColor: "#f3f4f6",
      color: "#364153",
    },
    2: {
      backgroundColor: "#fef9c2",
      color: "#a65f00",
    },
    3: {
      backgroundColor: "#dcfce7",
      color: "#008236",
    },
    4: {
      backgroundColor: "#ffe2e2",
      color: "#c10007",
    },
  };

  const statusStyle = STATUS_STYLES[idStatus] || STATUS_STYLES.default;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.titleRow}>
            <Text style={styles.title}>{title}</Text>

            <View
              style={{
                alignItems: "center",
                flexDirection: "row",
                justifyContent: "space-end",
                gap: 8,
              }}
            >
              <Text style={styles.badge}>{type}</Text>
              <Text
                style={[
                  styles.badgeStatus,
                  {
                    backgroundColor: statusStyle.backgroundColor,
                    color: statusStyle.color,
                  },
                ]}
              >
                {getStatus(idStatus)}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.cardsGrid}>
          {cards.map((card) => (
            <View key={card.title} style={styles.card}>
              <Text style={styles.cardValue}>{FormatInt(card.value ?? 0)}</Text>
              <Text style={styles.muted}>{card.title}</Text>
            </View>
          ))}
        </View>

        <View style={styles.campaignMessage}>
          <Text style={styles.sectionTitle}>Mensaje de campaña</Text>
          <Text>{nameCampaign || "---"}</Text>
        </View>

        <View style={styles.detailBox}>
          <Text style={styles.sectionTitle}>Detalles</Text>
          {details.map((item, index) => {
            const isNumeric =
              typeof item.value === "number" ||
              (typeof value === "string" &&
                item.value.trim() !== "" &&
                /^-?\d+(\.\d+)?$/.test(item.value));

            return (
              <View
                key={item.label}
                style={[
                  styles.detailRow,
                  index % 2 === 0 && styles.detailRowAlt,
                ]}
              >
                <Text style={styles.detailLabel}>{item.label}</Text>
                <Text>
                  {!isNumeric ? item.value : FormatInt(item.value) ?? 0}
                </Text>
              </View>
            );
          })}
        </View>

        <Text style={styles.footer}>
          Reporte generado el {dayjs().format("DD/MM/YYYY HH:mm")}
        </Text>
      </Page>
    </Document>
  );
};

CampaignDetailPdfDocument.propTypes = {
  title: PropTypes.string,
  type: PropTypes.string,
  idStatus: PropTypes.number,
  nameCampaign: PropTypes.string,
  data: PropTypes.object,
};

export default CampaignDetailPdfDocument;
